import { useEffect, useRef } from "react";
import "./MegahornAnimation.css";

export default function MegaHornAnimation({ playerRef, opponentRef, onComplete }) {
  const animRef = useRef(null);

  useEffect(() => {
    console.log('MegaHorn animation starting...');
    const attackerEl = playerRef.current;
    const defenderEl = opponentRef.current;
    const container = defenderEl?.closest(".battle-container");
    
    console.log('MegaHorn elements:', { attackerEl, defenderEl, container });
    
    if (!attackerEl || !defenderEl || !container) {
      console.log('MegaHorn missing elements, calling onComplete');
      onComplete();
      return;
    }

    // Get positions for both Pokemon
    const attackerRect = attackerEl.getBoundingClientRect();
    const defenderRect = defenderEl.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    // Calculate centers and positions
    const attackerCenterX = attackerRect.left - containerRect.left + attackerRect.width / 2;
    const attackerCenterY = attackerRect.top - containerRect.top + attackerRect.height / 2;
    const defenderCenterX = defenderRect.left - containerRect.left + defenderRect.width / 2;
    const defenderCenterY = defenderRect.top - containerRect.top + defenderRect.height / 2;

    // Calculate direction and distance for horn attack
    const dx = defenderCenterX - attackerCenterX;
    const dy = defenderCenterY - attackerCenterY;
    const distance = Math.sqrt(dx * dx + dy * dy);
    const angle = Math.atan2(dy, dx);

    // Track all created elements for cleanup
    const elements = [];
    const timers = [];

    // PHASE 1: PREPARATION AND CHARGING (0-400ms)
    // Initial attacker animation - charging stance
    attackerEl.style.transition = 'transform 0.3s ease-in-out, filter 0.3s ease-in-out';
    attackerEl.style.transform = 'scale(1.15) rotate(-8deg)';
    attackerEl.style.filter = 'brightness(1.3) drop-shadow(0 0 15px #8BC34A)';
    
    // Create horn charging aura around attacker
    const hornChargingAura = document.createElement('div');
    hornChargingAura.className = 'mega-horn-charging-aura';
    hornChargingAura.style.left = `${attackerCenterX}px`;
    hornChargingAura.style.top = `${attackerCenterY}px`;
    container.appendChild(hornChargingAura);
    elements.push(hornChargingAura);
    
    // Create energy buildup particles around attacker
    for (let i = 0; i < 15; i++) {
      const chargeTimer = setTimeout(() => {
        const chargeParticle = document.createElement('div');
        chargeParticle.className = 'mega-horn-charge-particle';
        
        const particleAngle = (i / 15) * Math.PI * 2;
        const particleDistance = 35 + Math.random() * 20;
        chargeParticle.style.left = `${attackerCenterX + Math.cos(particleAngle) * particleDistance}px`;
        chargeParticle.style.top = `${attackerCenterY + Math.sin(particleAngle) * particleDistance}px`;
        chargeParticle.style.setProperty('--charge-delay', `${i * 0.02}s`);
        chargeParticle.style.setProperty('--charge-rotation', `${Math.random() * 360}deg`);
        
        container.appendChild(chargeParticle);
        elements.push(chargeParticle);
        
        // Auto-remove particle
        const particleRemoveTimer = setTimeout(() => {
          if (chargeParticle.isConnected) {
            chargeParticle.remove();
          }
        }, 800);
        timers.push(particleRemoveTimer);
      }, i * 25);
      timers.push(chargeTimer);
    }
    
    // PHASE 2: HORN DASH ATTACK (400-700ms)
    const hornDashTimer = setTimeout(() => {
      // Create massive horn projectile
      const megaHorn = document.createElement('div');
      megaHorn.className = 'mega-horn-projectile';
      megaHorn.style.left = `${attackerCenterX}px`;
      megaHorn.style.top = `${attackerCenterY}px`;
      megaHorn.style.setProperty('--horn-angle', `${angle}rad`);
      megaHorn.style.setProperty('--horn-distance', `${distance}px`);
      container.appendChild(megaHorn);
      elements.push(megaHorn);
      animRef.current = megaHorn;
      
      // Create horn trail effect
      const hornTrail = document.createElement('div');
      hornTrail.className = 'mega-horn-trail';
      hornTrail.style.left = `${attackerCenterX}px`;
      hornTrail.style.top = `${attackerCenterY}px`;
      hornTrail.style.setProperty('--trail-angle', `${angle}rad`);
      hornTrail.style.setProperty('--trail-length', `${distance}px`);
      container.appendChild(hornTrail);
      elements.push(hornTrail);
      
      // Create speed lines during dash
      for (let i = 0; i < 8; i++) {
        const speedLineTimer = setTimeout(() => {
          const speedLine = document.createElement('div');
          speedLine.className = 'mega-horn-speed-line';
          
          const lineOffset = (i - 4) * 15;
          speedLine.style.left = `${attackerCenterX + Math.cos(angle + Math.PI/2) * lineOffset}px`;
          speedLine.style.top = `${attackerCenterY + Math.sin(angle + Math.PI/2) * lineOffset}px`;
          speedLine.style.setProperty('--line-angle', `${angle}rad`);
          speedLine.style.setProperty('--line-delay', `${i * 0.03}s`);
          
          container.appendChild(speedLine);
          elements.push(speedLine);
          
          const lineRemoveTimer = setTimeout(() => {
            if (speedLine.isConnected) {
              speedLine.remove();
            }
          }, 400);
          timers.push(lineRemoveTimer);
        }, i * 30);
        timers.push(speedLineTimer);
      }
      
    }, 400); // After charging phase
    
    timers.push(hornDashTimer);

    // PHASE 3: MASSIVE IMPACT (700-1100ms)
    const impactTimer = setTimeout(() => {
      // Massive horn impact explosion
      const hornImpact = document.createElement('div');
      hornImpact.className = 'mega-horn-impact';
      hornImpact.style.left = `${defenderCenterX}px`;
      hornImpact.style.top = `${defenderCenterY}px`;
      container.appendChild(hornImpact);
      elements.push(hornImpact);
      
      // Screen shake effect for powerful move
      const screenShake = document.createElement('div');
      screenShake.className = 'mega-horn-screen-shake';
      container.appendChild(screenShake);
      elements.push(screenShake);
      
      // Create impact shockwave rings
      for (let i = 0; i < 4; i++) {
        const shockwaveTimer = setTimeout(() => {
          const shockwave = document.createElement('div');
          shockwave.className = 'mega-horn-shockwave';
          shockwave.style.left = `${defenderCenterX}px`;
          shockwave.style.top = `${defenderCenterY}px`;
          shockwave.style.setProperty('--shockwave-delay', `${i * 0.1}s`);
          shockwave.style.setProperty('--shockwave-size', `${100 + i * 40}px`);
          
          container.appendChild(shockwave);
          elements.push(shockwave);
          
          const shockwaveRemoveTimer = setTimeout(() => {
            if (shockwave.parentNode) {
              shockwave.remove();
            }
          }, 600);
          timers.push(shockwaveRemoveTimer);
        }, i * 80);
        timers.push(shockwaveTimer);
      }
      
      // Make defender react with powerful hit animation
      defenderEl.classList.add('mega-horn-hit');
      defenderEl.style.filter = 'brightness(1.5) contrast(1.3) hue-rotate(60deg)';
      
      // Create horn fragment particles on impact
      for (let i = 0; i < 20; i++) {
        const fragment = document.createElement('div');
        fragment.className = 'mega-horn-fragment';
        
        const fragmentAngle = Math.random() * Math.PI * 2;
        const fragmentDistance = 20 + Math.random() * 50;
        fragment.style.left = `${defenderCenterX + Math.cos(fragmentAngle) * fragmentDistance}px`;
        fragment.style.top = `${defenderCenterY + Math.sin(fragmentAngle) * fragmentDistance}px`;
        
        fragment.style.setProperty('--fragment-rotation', `${Math.random() * 360}deg`);
        fragment.style.setProperty('--fragment-scale', `${0.5 + Math.random() * 1}`);
        fragment.style.setProperty('--drift-x', `${(Math.random() - 0.5) * 80}px`);
        fragment.style.setProperty('--drift-y', `${(Math.random() - 0.5) * 80}px`);
        
        container.appendChild(fragment);
        elements.push(fragment);
        
        const fragmentTimer = setTimeout(() => {
          if (fragment.parentNode) {
            fragment.remove();
          }
        }, 800 + Math.random() * 400);
        timers.push(fragmentTimer);
      }
      
      // Create powerful impact flash
      const impactFlash = document.createElement('div');
      impactFlash.className = 'mega-horn-impact-flash';
      impactFlash.style.left = `${defenderCenterX}px`;
      impactFlash.style.top = `${defenderCenterY}px`;
      container.appendChild(impactFlash);
      elements.push(impactFlash);
      
      // Reset defender after hit effect
      const defenderResetTimer = setTimeout(() => {
        defenderEl.classList.remove('mega-horn-hit');
        defenderEl.style.filter = '';
      }, 800);
      timers.push(defenderResetTimer);
      
    }, 700); // Impact timing
    
    timers.push(impactTimer);

    // PHASE 4: RECOIL AND RECOVERY (1100-1400ms)
    const recoilTimer = setTimeout(() => {
      // Attacker recoil from powerful attack
      attackerEl.style.transition = 'transform 0.4s ease-out, filter 0.4s ease-out';
      attackerEl.style.transform = 'scale(0.95) rotate(5deg)';
      attackerEl.style.filter = 'brightness(0.9)';
      
      // Create recoil effect
      const recoilEffect = document.createElement('div');
      recoilEffect.className = 'mega-horn-recoil';
      recoilEffect.style.left = `${attackerCenterX}px`;
      recoilEffect.style.top = `${attackerCenterY}px`;
      container.appendChild(recoilEffect);
      elements.push(recoilEffect);
      
    }, 1100);
    timers.push(recoilTimer);

    // Reset attacker position after animation
    const resetTimer = setTimeout(() => {
      attackerEl.style.transition = 'transform 0.3s ease-out, filter 0.3s ease-out';
      attackerEl.style.transform = '';
      attackerEl.style.filter = '';
    }, 1200);
    timers.push(resetTimer);

    // Complete animation and cleanup
    const completeTimer = setTimeout(() => {
      onComplete();
    }, 2000);
    timers.push(completeTimer);

    // Cleanup function
    return () => {
      // Clear all timers
      timers.forEach(timer => clearTimeout(timer));
      
      // Reset attacker
      if (attackerEl) {
        attackerEl.style.transition = '';
        attackerEl.style.transform = '';
        attackerEl.style.filter = '';
      }
      
      // Reset defender
      if (defenderEl) {
        defenderEl.classList.remove('mega-horn-hit');
        defenderEl.style.filter = '';
      }
      
      // Remove all created elements
      elements.forEach(element => {
        if (element && element.parentNode) {
          element.remove();
        }
      });
      
      // Remove any remaining effects
      const remainingElements = container.querySelectorAll(
        '.mega-horn-charging-aura, .mega-horn-charge-particle, .mega-horn-projectile, ' +
        '.mega-horn-trail, .mega-horn-speed-line, .mega-horn-impact, .mega-horn-screen-shake, ' +
        '.mega-horn-shockwave, .mega-horn-fragment, .mega-horn-impact-flash, .mega-horn-recoil'
      );
      remainingElements.forEach(el => el.remove());
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
}

MegaHornAnimation.moveType = "bug";
